/* Вход тренера (REG-2): телефон → код из СМС. Имя, фамилия и согласие с условиями —
   не здесь: новый аккаунт после входа попадает в Onboarding.tsx («Расскажите о себе»),
   у кого уже есть аккаунт — сразу в кабинет. В прототипе экрана нет — сделан в языке
   бренда: оранжевая заставка со знаком ТРЕНЕРГРАМ (срез 45°, как в design.html) и форма справа. */
import { api, codeHint, CountryCode, setSession, useCodeChannel, usePhone } from '@tg/app-core';
import { phoneView } from '@tg/domain';
import { useEffect, useRef, useState } from 'react';

const errText = (e: unknown) => (e instanceof Error && e.message && !/fetch/i.test(e.message) ? e.message : 'Нет связи с сервером — попробуйте ещё раз');

/* Вход и регистрация — модалками на лендинге (public/landing/auth.js, из tools/landing-auth.js): на проде /login
   ведёт туда (после «Выйти» — на лендинг без модалки). Эта форма остаётся для локальной разработки, где лендинга на «/» нет. */
let signedOut = false;
/** Выход из кабинета: дальше — просто лендинг, без модалки входа */
export function markSignedOut() {
  signedOut = true;
}

export function Login() {
  if (import.meta.env.PROD) {
    location.replace(signedOut ? '/' : '/#login');
    return null;
  }
  return <LoginForm />;
}

function LoginForm() {
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const ph = usePhone();
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(0);
  const [sentAt, setSentAt] = useState(0);
  const codeRef = useRef<HTMLInputElement>(null);
  const ch = useCodeChannel(step === 'code' ? ph.full : '', sentAt);

  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait(wait - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);
  useEffect(() => {
    if (step === 'code') codeRef.current?.focus();
  }, [step]);

  const request = async () => {
    if (!ph.ok) return setErr('Введите номер полностью');
    setBusy(true);
    setErr('');
    try {
      const r = await api.auth.requestCode.mutate({ phone: ph.full, app: 'trainer' });
      setWait(r.resendIn);
      setSentAt(Date.now());
      setStep('code');
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };
  const verify = async (c = code) => {
    if (c.length !== 4) return;
    setBusy(true);
    setErr('');
    try {
      /* Код партнёрской ссылки /p/КОД, запомненный лендингом — регистрация привяжет к партнёру */
      let partner: string | undefined;
      try {
        partner = JSON.parse(localStorage.getItem('tg.partner') || 'null') || undefined;
      } catch {
        /* хранилище недоступно */
      }
      const r = await api.auth.verify.mutate({ phone: ph.full, code: c, app: 'trainer', partner });
      setSession({ access: r.access, refresh: r.refresh, role: r.role, pid: r.pid });
    } catch (e) {
      setErr(errText(e));
      setCode('');
      codeRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth">
      <div className="auth-brand">
        <div className="auth-lg">
          <span>Тренерграм</span>
        </div>
        <p>Кабинет тренера: клиенты, программы и тренировки в одном месте.</p>
      </div>
      <div className="auth-side">
        <form
          className="auth-card"
          onSubmit={(e) => {
            e.preventDefault();
            step === 'phone' ? request() : verify();
          }}
        >
          <h1>{step === 'phone' ? 'Вход для тренера' : 'Код для входа'}</h1>
          <p className="lead">
            {step === 'phone' ? (
              'Введите номер телефона — пришлём код для входа.'
            ) : (
              <>
                Отправили код на {phoneView(ph.full)}.{' '}
                <button
                  type="button"
                  className="lnk"
                  onClick={() => {
                    setStep('phone');
                    setCode('');
                    setErr('');
                  }}
                >
                  Изменить номер
                </button>
              </>
            )}
          </p>
          {step === 'phone' ? (
            <div className="formfld">
              <span className="k">Телефон</span>
              <div className="inp tel-cc" data-cc-box>
                <CountryCode c={ph.c} onChange={ph.setCountry} />
                <input type="tel" inputMode="tel" autoComplete="tel-national" aria-label="Телефон" autoFocus value={ph.value} onChange={(e) => ph.onInput(e.target.value)} placeholder={ph.placeholder} />
              </div>
            </div>
          ) : (
            <div className="formfld">
              <span className="k">Код</span>
              <input
                ref={codeRef}
                className="inp auth-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={4}
                value={code}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                  setCode(v);
                  if (v.length === 4) verify(v);
                }}
                placeholder="••••"
              />
            </div>
          )}
          {step === 'code' && <div className={'auth-ch' + (ch.channel ? ' ' + ch.channel : '')}>{codeHint(ch)}</div>}
          {err && <div className="auth-err">{err}</div>}
          <button className="btn auth-go" disabled={busy || (step === 'phone' ? !ph.ok : code.length !== 4)}>
            {step === 'phone' ? 'Получить код' : 'Войти'}
          </button>
          {step === 'code' && (
            <div className="hint">
              {wait > 0 ? (
                `Отправить код ещё раз можно через ${wait} с`
              ) : (
                <button type="button" className="lnk" onClick={request}>
                  Отправить код ещё раз
                </button>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
